import { Button, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import useTaskRouterHandlers from '../../hooks/taskRouterHandlers/taskRouterHandlers.hooks';
import { cartActions } from '../../reducers/cartSlice';
import { resetDialogSelectedItem } from '../../reducers/dialogSlice';
import {
  EndSessionTransmissionMessage,
  RestaurantStaffInterventionStatus,
  messagingActions,
  resetHypothesisFrame,
  sendCancelOrder,
  ICancelOrderTransmissionMessage,
  AgentInterceptionType,
} from '../../reducers/messagingSlice';
import { orderActions, sendOrder } from '../../reducers/orderSlice';
import useMenuUnificationOrderHandlers from '../../hooks/menuUnification/useMenuUnificationOrderHandler.hooks';
import { resetAIControlState } from '../../redux/features/ai/ai.slice';
import {
  selectFinishOrderTimer,
  selectFinishOrderButtonDisableInterval,
  selectFeatureFlagSessionEndOrderActionChanges,
  selectFeatureFlagTimerResetOnCartUpdate,
  selectFeatureFlagHitl3,
  selectIsHitl7Enabled,
  selectFeatureFlagOrderStatusBanner,
} from '../../redux/features/config/config.selector';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import {
  selectFinalOrderSubmitted,
  selectIsPostCarExitTimerActive,
  selectOrderId,
  selectSessionId,
} from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  endSession,
  resetSBOrderStates,
  setFinalOrderSubmitted,
} from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import { TaskStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import {
  selectValidCartItems,
  selectCartItemsQuantity,
} from '../../selectors/cart';
import {
  selectIsStaffIntervention,
  selectRestaurantStaffIntervention,
  selectIsCarPresent,
} from '../../selectors/message';
import {
  selectAIIndicator,
  selectActiveRestaurantCode,
  selectActiveRestaurantIsUnsupervisedAI,
} from '../../selectors/restaurant';
import { OrderActionArgs } from '../../types';
import { activeCartSelector, isValidCartSelector } from '../../utils/cart';
import Colors from '../../utils/color';
import { HITL7_BOTTOM_SECTION_HEIGHT } from '../../utils/hitl3Spacing';
import { END_SESSION_REASON } from '../../utils/constants';
import logger from '../../utils/logger';
import DwindlingTimer from '../DwindlingTimer';
import { EventSources } from '../../constants/event';
import { v4 as uuidv4 } from 'uuid';
import {
  OrderPipelineState,
  selectFinalOrderInFlight,
  selectLastHITLOrderId,
  selectMenuModuleCartErrors,
  selectOrderError,
  selectOrderPipeline,
} from '../../selectors/order';
import useFinalOrderElapsed, {
  formatElapsed,
} from '../../hooks/useFinalOrderElapsed.hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  autofillAndSubmitFinalOrder,
  AutofillFinalOrderOutcome,
  AutofillFinalOrderSource,
  IAutofillFinalOrderResult,
  selectAutoSubmitBypassesAIAutoMode,
  selectNeedsAutofill,
} from '../../reducers/orderSlice.autofillFinal';
import { selectIsKillSwitchClicked } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectFeatureFlagAutofillFinalOrderOnIntervention,
  selectFeatureFlagAutofillFinalOrderOnAutoSubmit,
} from '../../redux/features/config/config.selector';
import { SessionEndReasons } from '../../redux/features/sessionBoundary/sessionBoundary.constants';
import { resetAutoComboState } from '../../redux/features/autoCombo/autoCombo.slice';
import ConfirmDialog from '../common/ConfirmDialog';

const useStyles = makeStyles((theme: Theme) => ({
  buttonWrapper: {
    height: '64px',
    padding: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
  },
  // hitl_3_0 only: the buttons live in the pinned RightSidebar footer directly
  // under the totals, so drop the fixed 64px height + heavy padding that left
  // dead space around them. Legacy keeps the original wrapper.
  buttonWrapperHitl3: {
    height: 'auto',
    // 4px top / 8px sides / 4px bottom — tight, even vertical spacing.
    padding: theme.spacing(0.5, 1, 0.5),
  },
  // When the post-car-exit countdown pill is showing, reserve room above the
  // buttons so the pill's bump — which straddles the top edge of the (short)
  // hitl_3_0 Finish button — isn't clipped by the footer's overflow:hidden.
  // Paired with timerButtonHitl3.
  buttonWrapperHitl3Timer: {
    paddingTop: theme.spacing(4),
  },
  finishButton: {
    position: 'relative',
    '&:hover >div': {
      backgroundColor: `${Colors.vidaLoca} !important`,
    },
  },
  timerButton: {
    position: 'absolute',
    top: '-20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '25px',
    width: '80px',
    height: '36px',
    backgroundColor: Colors.abbey,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: `${Colors.vidaLoca} !important`,
    },
  },
  // hitl_3_0: straddle the top edge of the (short) Finish button so the pill
  // reads as a rounded bump attached to it — poking above while overlapping just
  // the top strip, so both the pill's timer and the button's label stay visible.
  // Centered horizontally; paired with buttonWrapperHitl3Timer for the room above.
  // zIndex keeps the bump from being painted over by the button.
  timerButtonHitl3: {
    top: '-24px',
    bottom: 'auto',
    zIndex: 2,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  actionButton: {
    flex: 1,
    fontWeight: 'bold !important',
  },
  // Permanent Tooltip child wrapper around Finish Order: hosts hover while
  // the Button inside is disabled (a disabled button swallows pointer
  // events, muting the tooltip exactly during the finish-order lockout).
  // Takes the Button's slot in the flex bar; the Button fills it via flex:1.
  tooltipChildWrapper: {
    flex: 1,
    display: 'flex',
  },
  customizeDisabledButton: {
    backgroundColor: `${Colors.alto2} !important`,
  },
  customizeDisabledTimerButton: {
    backgroundColor: `${Colors.alto2} !important`,
  },
  progress: {
    position: 'absolute',
    bottom: 0,
  },
}));

export const OrderActions = () => {
  const classes = useStyles();
  // hitl_3_0 only: render the button labels as written (title case) instead of
  // MUI's default ALL-CAPS. Legacy keeps the uppercase look.
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  // Inputs for the sticky AI-status band on finish. `!isAIAutoMode` is the
  // durable "an agent has taken manual control of this order" signal (any manual
  // action flips it via sendAgentInterception), which is what the band reflects
  // — its text comes from the live aiStatus.message (e.g. "Agent Intercept"),
  // NOT the requiresTakeover/hardcoded branch. So we key the reset-skip off
  // isAIAutoMode + car presence.
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  // HITL-1653: the cover emit mirrors useAgentInterception's first-interception
  // condition, and this is its other half.
  const isAIEnabled = useAppSelector(selectAIIndicator);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const dispatch = useDispatch();
  const maxTimerSeconds = useAppSelector(selectFinishOrderTimer);
  const finishOrderButtonDisableInterval = useAppSelector(
    selectFinishOrderButtonDisableInterval
  );
  const isPostCarExitTimerActivated = useAppSelector(
    selectIsPostCarExitTimerActive
  );
  const isSessionEndOrderActionChangesEnabled = useAppSelector(
    selectFeatureFlagSessionEndOrderActionChanges
  );
  const isTimerResetOnCartUpdateEnabled = useAppSelector(
    selectFeatureFlagTimerResetOnCartUpdate
  );
  const isRestaurantUnsupervisedAI = useAppSelector(
    selectActiveRestaurantIsUnsupervisedAI
  );

  const cartActive = useSelector(activeCartSelector);
  const cartValid = useSelector(isValidCartSelector);

  const validCartItems = useSelector(selectValidCartItems);
  const cartItemsQuantity = useSelector(selectCartItemsQuantity);
  const restaurantCode = useSelector(selectActiveRestaurantCode);
  const orderSessionId = useSelector(
    (state: RootState) => state.order.currentSessionId
  );
  const messageSessionId = useSelector(
    (state: RootState) => state.messages.currentSessionId
  );
  const sessionId = useAppSelector(selectSessionId) || '';
  const isStaffIntervention = useSelector(selectIsStaffIntervention);
  const restaurantStaffIntervention = useSelector(
    selectRestaurantStaffIntervention
  );
  const isRestaurantStaffInterventionClicked =
    restaurantStaffIntervention !== RestaurantStaffInterventionStatus.initial;
  const { sendTaskStatus, isCurrentTaskFromTaskRouter, currentTaskStatus } =
    useTaskRouterHandlers();

  const isFinalOrderSubmitted = useSelector(selectFinalOrderSubmitted);

  const aiOrderId = useSelector(selectOrderId);
  const lastHITLOrderId = useSelector(selectLastHITLOrderId);
  const lastOrderId = aiOrderId || lastHITLOrderId;

  // HITL-1513 (D): the same derived fact the centre-top banner shows, put under
  // the pointer. Read-only — it gates a tooltip and a log line, and nothing
  // else. `inFlight` is false once POS has rejected the order, which is
  // deliberate: re-sending is then the correct recovery, not a duplicate.
  const finalOrder = useAppSelector(selectFinalOrderInFlight);
  const { elapsedMs } = useFinalOrderElapsed(
    finalOrder.sentAt,
    finalOrder.inFlight
  );

  // HITL-1527: the same tooltip also warns when the pipeline holds a known
  // error — a menu-module rejection (submitting will deterministically fail
  // again) or a POS error/rejection. The duplicate-final warning above wins
  // when both are true: it is the more destructive click.
  const orderPipeline = useAppSelector(selectOrderPipeline);
  const menuCartErrors = useAppSelector(selectMenuModuleCartErrors);
  const posOrderError = useAppSelector(selectOrderError);
  // Gated on the banner flag (review feedback): this leg is part of the
  // HITL-1527 surface, so flag off = the pre-banner tooltip exactly (the
  // duplicate-final warning below is !1581's and ships on its own).
  const isOrderStatusBannerEnabled = useAppSelector(
    selectFeatureFlagOrderStatusBanner
  );
  let orderWarning: string | null = null;
  if (!isOrderStatusBannerEnabled) {
    // no pipeline warnings when the banner surface is off
  } else if (orderPipeline?.state === OrderPipelineState.sendFailed) {
    const pathed = menuCartErrors.filter((e) => e.nodePath.length > 0);
    const first = pathed[0]?.nodePath[0];
    const more = pathed.length > 1 ? ` (+${pathed.length - 1} more)` : '';
    orderWarning = first
      ? `"${first}" was rejected by the menu${more} — submitting will fail again until it is fixed or removed.`
      : 'The last order could not be sent — submitting will fail again until the cart is fixed.';
  } else if (orderPipeline?.state === OrderPipelineState.rejected) {
    orderWarning =
      'POS already has a final order for this session — submitting again will be rejected.';
  } else if (orderPipeline?.state === OrderPipelineState.failed) {
    orderWarning = `POS reported an error for the last order${
      posOrderError ? ` (${posOrderError})` : ''
    } — review before finishing.`;
  }
  const showFinishOrderTooltip = finalOrder.inFlight || orderWarning !== null;

  const { sendAgentInterception } = useAgentInterception();

  // Reuse the same handlers interim orders use, so a failed FINAL submit
  // surfaces through the menu-module error UI (order-level banner + failing
  // node highlighted in the cart) and a successful one clears it.
  const {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  } = useMenuUnificationOrderHandlers();

  const [showCancelOrderConfirmDialog, setShowCancelOrderConfirmDialog] =
    useState(false);
  const [isFinishOrderButtonDisabled, setIsFinishOrderButtonDisabled] =
    useState(false);

  const MAX_TIMER_RESETS = 3;
  const [timerResetCount, setTimerResetCount] = useState(0);
  const [timerResetTrigger, setTimerResetTrigger] = useState(0);
  const prevValidCartItemsRef = useRef(validCartItems);
  const prevCartItemsQuantityRef = useRef(cartItemsQuantity);
  const cartChangedDuringIntervalRef = useRef(false);

  const resetStatesAfterFinalOrderSubmit = () => {
    dispatch(resetDialogSelectedItem());
    dispatch(resetHypothesisFrame());
    dispatch(resetAutoComboState());
    dispatch(resetSBOrderStates()); // Resetting session boundary order states: finalOrderSubmitted, orderMessageOrderId, checkCreated
    dispatch(cartActions.clearCart());
    dispatch(messagingActions.clearMessages());
    // HITL 3.0 only: the Pulse card's AI status band is always on screen, so it
    // must reset to its default alongside the transcript clear — otherwise it
    // strands the finished order's last status until the next car enters. Gated
    // on isHitl3 so the legacy AIStatusBar flow is untouched (it resets on the
    // next session's stage/menu fetch, as before). Uses resetAIControlState (not
    // the full resetAI) so the ai_session_state signals — Turn / Sentiment /
    // reused / express — SURVIVE a finish-while-car-present; they clear on the
    // next session start instead.
    //
    // EXCEPTION — once an agent has taken manual control of the order
    // (isAIAutoMode === false) and the car is STILL present, the AI status must
    // remain the visible state: an intercepted order stays intercepted for the
    // rest of that car's visit. So skip the control reset in that case — it would
    // reset aiStatus.message back to the default "OK, session is running
    // smoothly" (and flip isAIAutoMode back to auto), dropping the band's live
    // "Agent Intercept" status. The state still clears at the next-order /
    // session boundary, exactly as the non-intercept path does.
    const keepIntercept = !isAIAutoMode && isCarPresent;
    if (isHitl3 && !keepIntercept) dispatch(resetAIControlState());
    dispatch(orderActions.resetCurrentTransactionId());
    dispatch(orderActions.finishOrderResetValues());
    // Reset finish order button disabled state
    setIsFinishOrderButtonDisabled(false);
  };

  const handleAgentInterceptionAndTaskStatus = (
    actionType: AgentInterceptionType,
    taskStatus: TaskStatuses,
    args: OrderActionArgs,
    isAutoSubmitted: boolean = false
  ) => {
    if (!isAutoSubmitted && !args.skipAgentInterception) {
      sendAgentInterception({
        data: {
          type: actionType,
        },
      });
    }

    if (isCurrentTaskFromTaskRouter) {
      sendTaskStatus(taskStatus, args);
    }
  };
  const isKillSwitchClicked = useAppSelector(selectIsKillSwitchClicked);
  const isAutofillFinalOrderEnabled = useAppSelector(
    selectFeatureFlagAutofillFinalOrderOnIntervention
  );
  const isAutofillOnAutoSubmitEnabled = useAppSelector(
    selectFeatureFlagAutofillFinalOrderOnAutoSubmit
  );
  // For the trigger-time autofill gate (HITL-1562). `selectNeedsAutofill` is
  // read lazily off the live store INSTEAD of through useSelector: a
  // subscription would re-run the fill ladder on every cart edit for a value
  // only needed at the instant an auto submit fires.
  const store = useStore();

  // A staff intervention reaches us two ways — the agent's click, and the
  // `staff_intervention` event the click ALSO raises (plus the audio-detected
  // event, which arrives on its own). Either can land first, so neither effect
  // can dedupe against the other's state: checking
  // `isRestaurantStaffInterventionClicked` only closes click-then-event, and
  // leaves event-then-click submitting the final order twice (the POS rejects
  // the second with 7008 and the in-flight order loses its tracking).
  //
  // So the first arrival latches, and the second is a no-op whichever order they
  // come in. A ref and not state: both effects can run in the same commit, and a
  // state update would not be visible to the second one.
  //
  // KEYED BY SESSION, not a bare boolean. OrderActions is mounted in the
  // persistent RightBar, not per car, so a boolean would never clear — and
  // neither trigger clears itself either: `restaurantStaffIntervention` goes
  // initial -> open -> close and never back, so
  // `isRestaurantStaffInterventionClicked` stays true for the rest of the shift.
  // A boolean latch would therefore make EVERY car after the first a complete
  // no-op: no autofill, no final, and no cancel at all.
  const staffInterventionHandledFor = useRef<string | null>(null);
  const staffInterventionAlreadyHandled = () => {
    // Keyed on `sessionBoundary.sessionId`, and NOT on
    // `messages.currentSessionId`. The former is the car being worked on: it
    // holds for the whole of this car and only moves to the next car's id once
    // this car's order is submitted. The latter is the newest websocket session,
    // which on back-to-back cars can already be car 2 while the agent is still
    // on car 1 — keying on it, or falling back to it, would latch this car under
    // the next car's identity.
    if (staffInterventionHandledFor.current === sessionId) return true;
    staffInterventionHandledFor.current = sessionId;
    return false;
  };

  useEffect(() => {
    if (isRestaurantStaffInterventionClicked) {
      if (staffInterventionAlreadyHandled()) return;
      //When the "Restaurant Staff" button is clicked, a modal to be shown to select the reason
      // `isRestaurantStaffInterventionClicked` is passed so the task status is
      // actually reported: `sendTaskStatus` only reports when args carry one of
      // isCancelOrder / isFinishOrder / isStaffIntervention /
      // isRestaurantStaffInterventionClicked. Passing `{}` meant `canceled` was
      // never sent on this path, and the later isFinalOrderSubmitted effect then
      // resolved `currentTaskStatus === canceled ? canceled : completed` to
      // COMPLETED — reporting an agent completion for a car handed to staff.
      // `canceled` feeds agent performance reporting, so it has to land.
      handleStaffIntervention({ isRestaurantStaffInterventionClicked: true });
    }
  }, [isRestaurantStaffInterventionClicked]);

  useEffect(() => {
    // isFinalOrderSubmitted becomes true when:
    // 1. cancel order is clicked
    // 2. sendOrder in orderSlice when FF FinalOrderValidation is disabled
    // 3. check event with final true is succeed in sessionBoundary.slice for AI final order or HITL order when FF FinalOrderValidation is disabled
    // 4. final order status handlers(timeout, failed, succeed) in FinalOrderBackdrop when FF FinalOrderValidation is enabled
    if (isFinalOrderSubmitted) {
      resetStatesAfterFinalOrderSubmit();
      // A car handed to staff is NOT an agent completion, and `canceled` is what
      // agent performance reporting consumes.
      //
      // `currentTaskStatus` alone is no longer enough to tell. Before HITL-1551
      // a staff intervention reached here only via `cancelOrder`, which records
      // `canceled` before its own `setFinalOrderSubmitted(true)` — so the
      // ternary saw `canceled` and reported it. Now the intervention submits a
      // final order FIRST, and `sendOrder` sets `finalOrderSubmitted` on
      // Menu-Module success — so this effect runs mid-submit, before
      // `cancelOrder` has recorded anything, and the ternary resolved to
      // COMPLETED. The latch is the thing that already knows this car was handed
      // to staff, whatever the task router has caught up to.
      const isStaffInterventionCar =
        staffInterventionHandledFor.current === sessionId;
      const status =
        currentTaskStatus === TaskStatuses.canceled || isStaffInterventionCar
          ? TaskStatuses.canceled
          : TaskStatuses.completed;
      handleAgentInterceptionAndTaskStatus(
        AgentInterceptionType.FINISH_ORDER,
        status,
        { isFinishOrder: true },
        true // isAutoSubmitted
      );
    }
  }, [isFinalOrderSubmitted]);

  useEffect(() => {
    //When 'staff_intervention' event (or) info event with message 'StaffIntervention' is received via websocket
    if (isStaffIntervention) {
      //When "Restaurant Staff" button is clicked, StaffIntervention event is also being received.
      //To make sure that cancel order is not called twice — see the latch above.
      if (staffInterventionAlreadyHandled()) return;

      handleStaffIntervention(
        isCurrentTaskFromTaskRouter ? { isStaffIntervention: true } : {}
      );
    }
  }, [isStaffIntervention]);

  useEffect(() => {
    if (isKillSwitchClicked) {
      // The handler is async since HITL-1562, so the submit must be AWAITED
      // before the session is torn down: `endSession` clears
      // sessionBoundary.sessionId immediately (fillEndSessionValues), which
      // would strand the autofill outcome metric mid-flight and record the
      // FINISH_ORDER end-session against an already-ended session. Same
      // teardown-must-not-overtake-the-order rule handleStaffIntervention
      // enforces with its await. try/finally so the teardown can never be
      // lost to a submit failure — bailing out is this button's whole job.
      (async () => {
        try {
          if (!isSessionEndOrderActionChangesEnabled) {
            await autoSubmitHandler(AutofillFinalOrderSource.killSwitch);
          }
        } catch (error) {
          logger.error({
            message:
              'Kill-switch auto submit failed; ending the session anyway',
            sessionId,
            error: String(error),
          });
        } finally {
          dispatch(endSession(SessionEndReasons.killSwitchClicked));
        }
      })();
    }
  }, [isKillSwitchClicked, isSessionEndOrderActionChangesEnabled]);

  useEffect(() => {
    if (!isPostCarExitTimerActivated) {
      setTimerResetCount(0);
      setTimerResetTrigger(0);
      cartChangedDuringIntervalRef.current = false;
    }
  }, [isPostCarExitTimerActivated]);

  useEffect(() => {
    const cartActuallyChanged =
      prevValidCartItemsRef.current !== validCartItems ||
      prevCartItemsQuantityRef.current !== cartItemsQuantity;
    prevValidCartItemsRef.current = validCartItems;
    prevCartItemsQuantityRef.current = cartItemsQuantity;

    if (
      cartActuallyChanged &&
      isTimerResetOnCartUpdateEnabled &&
      isPostCarExitTimerActivated &&
      isRestaurantUnsupervisedAI
    ) {
      cartChangedDuringIntervalRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Gates are intentionally excluded: we only care about their values at the
    // moment a cart change is detected, not when the gates themselves flip.
  }, [validCartItems, cartItemsQuantity]);

  useEffect(() => {
    if (isFinishOrderButtonDisabled && finishOrderButtonDisableInterval > 0) {
      const timer = setTimeout(() => {
        setIsFinishOrderButtonDisabled(false);
      }, finishOrderButtonDisableInterval);

      return () => clearTimeout(timer);
    }
  }, [isFinishOrderButtonDisabled, finishOrderButtonDisableInterval]);

  const sendEndSession = (reason: string) => {
    if (restaurantCode) {
      const payload: Partial<EndSessionTransmissionMessage> = {
        data: {
          restaurant_code: restaurantCode,
          session_id: orderSessionId || messageSessionId,
        }, // still end the session if there is no order session yet
        metadata: reason,
      };
      dispatch(messagingActions.sendEndSession(payload as any));
    }
  };

  const cancelOrder = (args: OrderActionArgs) => {
    const { isCancelOrder, isAutoSubmitted } = args;
    logger.debug({
      message: 'Call cancelOrder',
      isCancelOrder,
      isAutoSubmitted,
    });

    handleAgentInterceptionAndTaskStatus(
      AgentInterceptionType.CANCEL_ORDER,
      TaskStatuses.canceled,
      args,
      isAutoSubmitted
    );

    if (isCancelOrder && lastOrderId) {
      const payload: Partial<ICancelOrderTransmissionMessage> = {
        data: {
          check_id: '-1',
          source: EventSources.prestoVoice,
          request_id: uuidv4(),
          store_id: restaurantCode,
          order_id: lastOrderId,
        },
      };
      dispatch(sendCancelOrder(payload as any));
    }
    sendEndSession(END_SESSION_REASON.CANCEL_ORDER);
    dispatch(setFinalOrderSubmitted(true));
  };

  /**
   * Staff intervention: fill the cart's empty required slots and submit the
   * final order, then end the session exactly as a cancel does (HITL-1551).
   *
   * The submit is AWAITED before the session is ended. The cart snapshot would
   * survive either way — `createAsyncThunk` and `sendOrder` both read state
   * before their first `await` — but the teardown must not overtake the order:
   * `end_session` carries reason `cancel_order`, and a backend that voids on it
   * would cancel the order we just placed. Waiting also means the outcome metric
   * is emitted while the session still exists, rather than after it ends.
   *
   * `cancelOrder` is then called with the teardown untouched. Threading this
   * feature through it instead — an `autofillAndSubmitFinal` flag, a deferred
   * teardown, a race and a timeout — is what produced most of this MR's review
   * findings; the teardown is not ours to re-implement or defer.
   *
   * THE INTERCEPTION GOES OUT FIRST (HITL-1578). In an unsupervised-AI
   * restaurant the backend order handler accepts a non-AI order only if an
   * `agent_interception` transaction already exists for the session — and on
   * this path the session's first interception used to be the one `cancelOrder`
   * emits, AFTER the awaited submit. On any escalated car the agent never
   * touched before staff spoke, the final order therefore always reached the
   * handler before the interception did and was silently discarded (session
   * c0e58a96, 292 ms apart) — while the autofill metric still reported
   * `filled_and_submitted`, since the Menu Module had accepted it. So the
   * interception is sent before the submit, and `cancelOrder` is told not to
   * re-send it (`skipAgentInterception`); everything else it does — task
   * status, end_session, the submit latch — is unchanged. Sending it early
   * also ends AI auto mode client-side, so a late hypothesis cannot rebuild
   * the cart out from under the fill.
   *
   * The kill switch is checked HERE, not inside the thunk, so with the flag off
   * the intervention is literally `cancelOrder(args)` and nothing else — one
   * interception, sent by `cancelOrder`, as before.
   */
  const handleStaffIntervention = async (args: OrderActionArgs) => {
    if (isAutofillFinalOrderEnabled) {
      // Deliberately still CANCEL_ORDER, not HITL-1653's AUTO_SUBMIT (review
      // round 1, #3): this is the one cover with production evidence behind
      // it, and whether the backend accepts a type it has never received is
      // unconfirmed. Retype it only after the order handler's owners confirm
      // the cover check is type-agnostic.
      sendAgentInterception({
        data: {
          type: AgentInterceptionType.CANCEL_ORDER,
        },
      });
      await dispatch(
        autofillAndSubmitFinalOrder({
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );
      cancelOrder({ ...args, skipAgentInterception: true });
      return;
    }
    cancelOrder(args);
  };

  const finishOrder = (args: OrderActionArgs) => {
    const { isAutoSubmitted, skipSendOrder, bypassAIAutoMode } = args;

    handleAgentInterceptionAndTaskStatus(
      AgentInterceptionType.FINISH_ORDER,
      TaskStatuses.completed,
      { ...args, isFinishOrder: true },
      isAutoSubmitted
    );

    sendEndSession(END_SESSION_REASON.FINISH_ORDER);
    // HITL-1562: on the autofill path the final order was ALREADY submitted by
    // autofillAndSubmitFinalOrder (awaited in autoSubmitHandler), so submitting
    // here again would be the exact duplicate FINAL_ORDER_DUPLICATE_SUBMIT
    // measures. Everything else — task status, end_session, the lockout —
    // still runs.
    if (!skipSendOrder) {
      dispatch(
        sendOrder({
          isFinal: true,
          // HITL-1607: set only by the auto-submit path. The click path leaves
          // it undefined — the interception dispatched above already cleared
          // isAIAutoMode, so the guard was never in its way.
          bypassAIAutoMode,
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );
    }

    // Disable the finish order button for the configured interval
    if (finishOrderButtonDisableInterval > 0) {
      setIsFinishOrderButtonDisabled(true);
    }
  };

  // HITL-1513 (D). Nothing here prevents the duplicate — the button stays
  // enabled and the click path is untouched — so this token is the deliverable's
  // only feedback loop. It answers the one question that matters after ship:
  // did making the state visible change agent behaviour? A flat `click` rate
  // means the warning is not landing, and the answer is then a confirm dialog,
  // decided against data rather than re-argued from a hunch.
  //
  // warn, not error: the app carries on. POS rejects the second final with 7008
  // and tracking for the order actually in flight is lost, but nothing breaks.
  const logDuplicateFinalSubmit = (source: 'click' | 'auto') => {
    if (!finalOrder.inFlight) return;

    logger.warn({
      message: 'FINAL_ORDER_DUPLICATE_SUBMIT',
      source,
      sessionId,
      requestId: finalOrder.requestId,
      // Read fresh rather than from the once-a-second display value, so the
      // logged number is the real gap at the moment of the click.
      elapsedMs: finalOrder.sentAt
        ? Date.now() - new Date(finalOrder.sentAt).getTime()
        : null,
    });
  };

  // HITL-1527 (from the HITL-1510 incident): a final submitted while the cart
  // holds a known menu-module rejection fails deterministically — the same
  // validator that rejected the sync rejects the final. Warned (tooltip
  // above), never blocked; this token measures whether the warning lands,
  // exactly like FINAL_ORDER_DUPLICATE_SUBMIT does for duplicates.
  const logFinalSubmitWithKnownRejection = (source: 'click' | 'auto') => {
    if (orderPipeline?.state !== OrderPipelineState.sendFailed) return;

    logger.warn({
      message: 'FINAL_SUBMIT_WITH_KNOWN_REJECTION',
      source,
      sessionId,
      errors: menuCartErrors.map(
        (e) => `${e.reasonCode}: ${e.nodePath.join(' > ') || e.message}`
      ),
    });
  };

  const handleFinishOrderClick = () => {
    logDuplicateFinalSubmit('click');
    logFinalSubmitWithKnownRejection('click');
    finishOrder({});
  };

  const handleCancelOrderClick = () => {
    setShowCancelOrderConfirmDialog(true);
  };

  const handleCancelOrderConfirm = () => {
    setShowCancelOrderConfirmDialog(false);
    cancelOrder({ isCancelOrder: true });
  };

  const autoSubmitHandler = async (source: AutofillFinalOrderSource) => {
    // The ship-metric tokens (FINAL_ORDER_DUPLICATE_SUBMIT /
    // FINAL_SUBMIT_WITH_KNOWN_REJECTION) are emitted at most ONCE per handler
    // run: both reads come from the same render snapshot, so re-logging on the
    // HITL-1562 fall-through would double-count the exact metric those tokens
    // exist to keep honest.
    let submitTelemetryLogged = false;
    // HITL-1607: whether this auto submit may pass sendOrder's isAIAutoMode
    // guard — the kill switch, minus the quantity-hold exclusion (the gate has
    // the full safety story: no AI send survives a car exit, an AI-finalized
    // car's cart was already cleared, and POSLINK rejects a duplicate final).
    // Read once, at fire time, off the live store (like the autofill gate
    // below), and used by BOTH send branches: the autofill thunk and the
    // no-fill finishOrder fall-through send with the same authority, or the
    // fix splits on cart shape.
    const bypassAIAutoMode = selectAutoSubmitBypassesAIAutoMode(
      store.getState()
    );
    // HITL-1653: a bypassed send needs interception COVER, not just passage.
    // `bypassAIAutoMode` gets the order past sendOrder's client-side guard, but
    // on an unsupervised-AI restaurant the backend order handler discards a
    // non-AI order unless the session already has interception cover
    // (HITL-1578, session c0e58a96 — an escalation alone is not cover). On the
    // cars this bypass exists for — escalated, agent-untouched — no
    // interception has ever gone out, so the bypassed final was accepted by
    // the Menu Module and then silently dropped before the POS.
    //
    // Called from the TWO BRANCHES THAT SEND, never from the handler top: the
    // auto-cancel branch sits between them, and a cover emitted on a car that
    // only cancels is a takeover-analytics row for a session that submitted
    // nothing (review round 1, #1). AUTO_SUBMIT means "a send is covered", not
    // "a bypass was authorised".
    //
    // `isAIAutoMode` still true IS "no cover exists": the only thing that
    // clears it is the sendAgentInterception dispatch itself, so the check is
    // self-deduping — any car the agent touched already sent one, and the
    // fall-through's second call after the autofill branch's first is a no-op.
    // The `bypassAIAutoMode` term is load-bearing too: without the bypass the
    // guard should eat the send, and emitting here would clear the very flag
    // the guard reads — a cover emit must never turn into a force-send.
    //
    // Emits the SAME PAIR the production-proven covers put on the wire
    // (review round 1, #2): every cover with field evidence — HITL-1578's
    // CANCEL_ORDER, the TTS_SENT-covered sessions — went through
    // useAgentInterception, which sends agent_interception AND
    // agent_first_interception. Which of the two the unsupervised-AI order
    // handler reads is unconfirmed, so this path replicates the proven shape
    // rather than betting on half of it. The hook itself is still deliberately
    // skipped for its third effect only: updateTaskStatus(agentInterception),
    // a false agent-activity signal on a path with no human.
    const emitAutoSubmitCover = () => {
      if (!bypassAIAutoMode || !selectIsAIAutoMode(store.getState())) return;
      logger.info({
        message: 'AUTO_SUBMIT_INTERCEPTION_COVER: emitted before bypassed send',
        sessionId,
        source,
      });
      dispatch(
        messagingActions.sendAgentInterception({
          data: { type: AgentInterceptionType.AUTO_SUBMIT },
        })
      );
      // Same condition as the hook's (isAIEnabled && isAIAutoMode) — the
      // auto-mode half is the early return above.
      if (isAIEnabled) {
        dispatch(
          messagingActions.sendAgentFirstInterception({
            data: { type: AgentInterceptionType.AUTO_SUBMIT },
          })
        );
      }
    };
    // HITL-1562: when the cart has fillable holes, the pre-fill validity count
    // below is the wrong judge — a cart whose EVERY item is incomplete counts
    // as zero valid items, and the whole order used to be thrown away. Fill
    // and submit first (the thunk owns the send). The gate keeps every cart
    // that needs no filling — the large majority — on the unchanged branch
    // below, and `skipSendOrder` is legal ONLY because the thunk just ran.
    if (
      isAutofillOnAutoSubmitEnabled &&
      selectNeedsAutofill(store.getState())
    ) {
      logger.info({
        message: `Auto submitting the order after autofill (${source}).`,
        sessionId,
      });
      logDuplicateFinalSubmit('auto');
      logFinalSubmitWithKnownRejection('auto');
      submitTelemetryLogged = true;
      emitAutoSubmitCover();
      const result = (
        (await dispatch(
          autofillAndSubmitFinalOrder({
            source,
            // Computed at the top of the handler (HITL-1607).
            bypassAIAutoMode,
            menuUnificationOrderFailureCallback,
            menuUnificationOrderSuccessCallback,
          })
        )) as any
      )?.payload as IAutofillFinalOrderResult | undefined;

      const shipped =
        result?.outcome === AutofillFinalOrderOutcome.filledAndSubmitted ||
        result?.outcome === AutofillFinalOrderOutcome.submittedUnchanged ||
        result?.outcome === AutofillFinalOrderOutcome.submittedIncomplete;
      if (shipped) {
        finishOrder({ isAutoSubmitted: true, skipSendOrder: true });
        return;
      }
      // Nothing reached the POS (not_submitted, a race on the flag, or the
      // thunk itself rejected). FALL THROUGH, do not cancel: the branch below
      // decides finish-vs-cancel from the same pre-fill `validCartItems`
      // snapshot today's code uses (captured at render, before the fill
      // mutated the cart), so a failed fill degrades to EXACTLY today's
      // behaviour for this cart shape. An early cancel here turned a mixed
      // cart — which today ships its valid items — into an order that ships
      // nothing, the opposite of what this feature is for. Costs accepted and
      // documented in the ticket: the finish sub-branch re-sends (a retry —
      // nothing landed the first time), and it re-sends the FILLED cart, so a
      // rejection caused by a filled 86'd option may repeat where today's
      // valid-subset send would have passed.
    }

    const validItemsCount = Object.values(validCartItems).length;
    if (validItemsCount || isFinalOrderSubmitted) {
      const info = validItemsCount
        ? ''
        : ` Cart had valid item${validItemsCount > 1 ? 's' : ''}`;
      // Carries `bypassAIAutoMode` because this branch never enters the
      // autofill thunk, so the AUTOFILL_FINAL_ORDER_METRIC's
      // bypass_ai_auto_mode (schema 3.1) does not cover it — and this is the
      // MAJORITY branch: the gate above keeps every cart needing no fill on
      // it. Without this token, a complete-cart car exit whose final never
      // lands is indistinguishable in New Relic between "guard blocked it"
      // and "the send failed".
      logger.info({
        message: `Auto submitting the order.${info}`,
        sessionId,
        source,
        bypassAIAutoMode,
        validItemsCount,
      });
      // The remaining hole, measured rather than closed: a post-car-exit timer
      // expiring during a stuck check produces the same duplicate with no human
      // present. Blocking automated session completion on a hunch would be
      // worse than the bug, so this path is logged and left alone.
      if (!submitTelemetryLogged) {
        logDuplicateFinalSubmit('auto');
        logFinalSubmitWithKnownRejection('auto');
      }
      // HITL-1607: on an escalated car the agent never touched, isAIAutoMode
      // is still true and finishOrder's own sendOrder would be silently
      // dropped by the guard — the complete-cart twin of the gap the autofill
      // branch above closes. HITL-1653 covers that send too (no-op when the
      // autofill branch above already emitted, or the agent already acted).
      emitAutoSubmitCover();
      finishOrder({ isAutoSubmitted: true, bypassAIAutoMode });
    } else {
      logger.info({
        message: 'Auto canceling the order',
        sessionId,
      });
      cancelOrder({ isAutoSubmitted: true });
    }
  };

  const handleTimerExpired = () => {
    if (
      cartChangedDuringIntervalRef.current &&
      timerResetCount < MAX_TIMER_RESETS
    ) {
      cartChangedDuringIntervalRef.current = false;
      setTimerResetCount((prev) => prev + 1);
      setTimerResetTrigger((prev) => prev + 1);
      logger.debug({
        message: `Resetting post car exit timer on cart update (${
          timerResetCount + 1
        }/${MAX_TIMER_RESETS})`,
        sessionId,
      });
    } else {
      // Deliberately not awaited — nothing runs after this to race the submit
      // — but a rejection must not surface as an unhandledrejection with the
      // order's fate undiagnosable.
      autoSubmitHandler(AutofillFinalOrderSource.postCarExitTimer).catch(
        (error) =>
          logger.error({
            message: 'Post-car-exit auto submit failed',
            sessionId,
            error: String(error),
          })
      );
    }
  };

  return (
    <>
      <div
        id="orderActions"
        className={[
          classes.buttonWrapper,
          isHitl3 && classes.buttonWrapperHitl3,
          isHitl3 &&
            isPostCarExitTimerActivated &&
            classes.buttonWrapperHitl3Timer,
        ]
          .filter(Boolean)
          .join(' ')}
        // HITL-1665: the same shared height the Copilot strip and the typing
        // box take, so the three bottom sections' top rules land on one line.
        // Less the 1px rule, which this section carries on its footer PARENT
        // rather than on the row itself — the other two own their rule, so they
        // subtract it via border-box and this one subtracts it here.
        //
        // A minimum, not an exact height, because this row alone has to be able
        // to grow: the post-car-exit countdown pill reserves space above the
        // buttons (buttonWrapperHitl3Timer), and pinning the height would clip
        // it under the footer's overflow:hidden.
        //
        // Inline rather than a new makeStyles rule: adding a rule here
        // renumbers JSS class names in far-away snapshots, and `undefined` with
        // the flag off leaves this element byte-identical.
        style={
          isHitl7
            ? {
                minHeight: HITL7_BOTTOM_SECTION_HEIGHT - 1,
                boxSizing: 'border-box',
              }
            : undefined
        }
      >
        <Button
          onClick={handleCancelOrderClick}
          variant="contained"
          disabled={!restaurantCode}
          color="error"
          className={classes.actionButton}
          sx={{ textTransform: isHitl3 ? 'none' : undefined }}
        >
          Cancel Order
        </Button>
        {/* HITL-1513 (D): hover-gated, not pinned open. A pinned tooltip sat
            over the totals for the whole window, and the centre-top banner
            already carries the same fact unmissably — so the persistent overlay
            was paying a real cost to repeat something the agent can already see.
            What it gives up is the fast-click case: a pointer that lands and
            clicks inside MUI's enterDelay never sees this. The banner is the
            cover for that, which is why C ships first and why it is the primary
            surface rather than this.

            The empty title when nothing is outstanding is what switches it
            off: MUI renders no tooltip for ''. The Button sits in a PERMANENT
            <span> wrapper (review feedback): during the post-Finish lockout
            the button is disabled while finalOrder.inFlight is still true —
            a non-empty title on a disabled child, which MUI warns about and
            which loses the tooltip (a disabled button swallows hover). The
            span hosts the hover instead. Permanent, never conditional: a
            conditional wrapper would remount the Button subtree on every AI
            final, restarting the DwindlingTimer inside it and moving when
            auto-submit fires — a control-flow change this story must not
            have. */}
        <Tooltip
          // Stable id so the Button below can carry its own aria-describedby:
          // with the <span> wrapper hosting the tooltip, MUI wires the
          // description to the span — but focus lands on the BUTTON, so the
          // button must reference the tooltip itself or a screen reader never
          // hears the warning.
          id="finish-order-warning-tooltip"
          placement="top"
          arrow
          // describeChild, so the tooltip attaches as aria-describedby rather
          // than replacing the button's accessible name. Without it a screen
          // reader announces the warning INSTEAD of "Finish Order" while it is
          // open — the control loses its name exactly when it matters.
          describeChild
          // Listeners off when there is nothing to say. An empty title already
          // keeps MUI from opening, but without these MUI still mirrors that ''
          // onto the button as a bare title="" attribute on every session.
          disableHoverListener={!showFinishOrderTooltip}
          disableFocusListener={!showFinishOrderTooltip}
          disableTouchListener={!showFinishOrderTooltip}
          title={
            finalOrder.inFlight ? (
              // Kept a ReactNode rather than a plain string even though it is
              // now one run of prose: for a string title MUI also mirrors it
              // onto the button's native `title` attribute, which would show a
              // second, OS-drawn tooltip underneath this one.
              <>
                {/* Neutral on WHO submitted: since HITL-1527 preserves the
                    final request through the finish-order reset, this fires
                    for the agent's own final too — "AI submitted" would be
                    wrong half the time. */}
                {finalOrder.leg === 'confirming'
                  ? `The restaurant is confirming the final order (${formatElapsed(
                      elapsedMs
                    )}).`
                  : `A final order was submitted ${formatElapsed(
                      elapsedMs
                    )} ago — waiting for the restaurant.`}{' '}
                {/* "duplicate", never "resubmit": resubmit sounds idempotent,
                    like a retry that might help, and invites the very click
                    this exists to discourage. */}
                Clicking now sends a duplicate.
              </>
            ) : orderWarning !== null ? (
              // Same ReactNode-not-string rule as above: a string title gets
              // mirrored onto the button's native `title`, doubling the
              // tooltip.
              <>{orderWarning}</>
            ) : (
              ''
            )
          }
        >
          <span className={classes.tooltipChildWrapper}>
            <Button
              onClick={handleFinishOrderClick}
              aria-describedby={
                showFinishOrderTooltip
                  ? 'finish-order-warning-tooltip'
                  : undefined
              }
              variant="contained"
              color="success"
              disabled={
                !cartActive || !cartValid || isFinishOrderButtonDisabled
              }
              className={`${classes.finishButton} ${classes.actionButton} ${
                !cartValid || isFinishOrderButtonDisabled
                  ? classes.customizeDisabledButton
                  : ''
              }`}
              sx={{ textTransform: isHitl3 ? 'none' : undefined }}
            >
              Finish Order
              {isPostCarExitTimerActivated && (
                <div
                  className={[
                    classes.timerButton,
                    isHitl3 && classes.timerButtonHitl3,
                    (!cartValid ||
                      !cartActive ||
                      isFinishOrderButtonDisabled) &&
                      classes.customizeDisabledTimerButton,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <DwindlingTimer
                    maxTime={maxTimerSeconds}
                    timeoutCallback={handleTimerExpired}
                    resetTrigger={timerResetTrigger}
                  />
                </div>
              )}
            </Button>
          </span>
        </Tooltip>
      </div>
      {showCancelOrderConfirmDialog && (
        <ConfirmDialog
          open={showCancelOrderConfirmDialog}
          content="Do you want to cancel the order?"
          onClose={() => setShowCancelOrderConfirmDialog(false)}
          onConfirm={handleCancelOrderConfirm}
          closeBtnText="No"
          confirmBtnText="Yes"
        />
      )}
    </>
  );
};
